import { isString } from "@common/helpers/typeGuards";
import { PlaidConnectionErrors } from "../constants";
import { FinancialAccountSourceType } from "../types/financialAccount";
import {
    ConnectionProviderType,
    FinancialConnection,
    FinancialConnectionBase,
} from "../types/financialConnection";
import { PlaidConnection } from "../types/plaidConnection";
import { IntegrationConnection } from "../types/integrationConnection";

/**
 * Type for PlaidConnection with Base relations.
 * Used by type guards when working with entities.
 */
type PlaidConnectionLike = FinancialConnectionBase & {
    bankName: string;
    bankLogoUrl: string | null;
    institutionId: string | null;
    historicalDataProvidedManually: boolean;
    error: PlaidConnectionErrors | null;
    statementsApiSupported: boolean | null;
    statementsApiEnabled: boolean | null;
};

interface FinancialConnectionSettings {
    multipleAccountsPerConnection: boolean;
    label: string | ((connection: FinancialConnection) => string);
    financialAccountSourceType: FinancialAccountSourceType;
}

export const FINANCIAL_CONNECTIONS_SETTINGS: Record<
    ConnectionProviderType,
    FinancialConnectionSettings
> = {
    [ConnectionProviderType.PLAID]: {
        multipleAccountsPerConnection: true,
        label: (connection) => (connection as PlaidConnection).bankName,
        financialAccountSourceType: FinancialAccountSourceType.PLAID,
    },
    [ConnectionProviderType.STRIPE]: {
        multipleAccountsPerConnection: false,
        label: "Stripe",
        financialAccountSourceType: FinancialAccountSourceType.STRIPE,
    },
    [ConnectionProviderType.GUSTO]: {
        multipleAccountsPerConnection: false,
        label: "Gusto",
        financialAccountSourceType: FinancialAccountSourceType.GUSTO,
    },
    [ConnectionProviderType.MERCURY]: {
        multipleAccountsPerConnection: true,
        label: "Mercury",
        financialAccountSourceType: FinancialAccountSourceType.MERCURY,
    },
    [ConnectionProviderType.RAMP]: {
        multipleAccountsPerConnection: true,
        label: "Ramp",
        financialAccountSourceType: FinancialAccountSourceType.RAMP,
    },
};

export function isPlaidConnection(
    connection: FinancialConnectionBase,
): connection is PlaidConnectionLike {
    return connection.connectionProvider === ConnectionProviderType.PLAID;
}

export function isFixablePlaidConnectionError(
    error: PlaidConnectionErrors | null,
): boolean {
    return (
        error !== PlaidConnectionErrors.ITEM_NOT_SUPPORTED &&
        error !== PlaidConnectionErrors.ITEM_REMOVED
    );
}

export function getNonFixablePlaidConnectionError(
    connection: FinancialConnectionBase | IntegrationConnection,
): PlaidConnectionErrors | null {
    if (
        isFinancialConnection(connection) &&
        isPlaidConnection(connection) &&
        !isFixablePlaidConnectionError(connection.error)
    ) {
        return connection.error;
    }
    return null;
}

export function isNonFixablePlaidConnection(
    connection: FinancialConnectionBase | IntegrationConnection,
): boolean {
    return getNonFixablePlaidConnectionError(connection) !== null;
}

export function getConnectionLabel(
    connection: FinancialConnectionBase,
): string {
    const labelProvider =
        FINANCIAL_CONNECTIONS_SETTINGS[connection.connectionProvider].label;

    return isString(labelProvider)
        ? labelProvider
        : labelProvider(connection as FinancialConnection);
}

export function isFinancialConnection(
    connection: FinancialConnectionBase | IntegrationConnection,
): connection is FinancialConnectionBase {
    return "connectionProvider" in connection;
}
